// Opens the chat a tapped push notification points to, even if the tap arrives before React, the router,
// or auth are ready. Started from index.js. The service worker (public/push-sw.js) both messages open
// windows and stores the tap in Cache Storage, so a missed message is still picked up on load/resume.
export const PENDING_CACHE_NAME = 'fm-push-navigation';
export const PENDING_KEY = '/__push-navigation/pending';
export const PENDING_MAX_AGE_MS = 2 * 60 * 1000;
// A tap waiting on sign-in survives the full-page Auth0 redirect here (same tab), long enough to sign in.
export const DEFERRED_STORAGE_KEY = 'pushNavigationDeferred';
export const DEFERRED_MAX_AGE_MS = 15 * 60 * 1000;

let container = null;
let bufferedUrl = null;
let activeHandler = null;
const handledIds = new Set();

/** Only in-app chat paths, never arbitrary URLs. */
export const isChatUrl = (url) => typeof url === 'string'
  && (url === '/chat' || url.startsWith('/chat/') || url.startsWith('/chat?'));

const saveDeferred = (url, id) => {
  try {
    sessionStorage.setItem(DEFERRED_STORAGE_KEY, JSON.stringify({ url, id: id || null, createdAt: Date.now() }));
  } catch {
    // Storage unavailable; the in-memory buffer still covers this page.
  }
};

const clearDeferred = () => {
  try {
    sessionStorage.removeItem(DEFERRED_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
};

/** Restores a tap that was still waiting when the previous page unloaded (e.g. redirected to sign in). */
const restoreDeferred = () => {
  try {
    const deferred = JSON.parse(sessionStorage.getItem(DEFERRED_STORAGE_KEY) || 'null');
    if (deferred && isChatUrl(deferred.url) && Date.now() - deferred.createdAt <= DEFERRED_MAX_AGE_MS) {
      bufferedUrl = deferred.url;
      if (deferred.id) handledIds.add(deferred.id);
    } else if (deferred) {
      clearDeferred();
    }
  } catch {
    clearDeferred();
  }
};

const deliver = (url, id) => {
  if (!isChatUrl(url)) return false;
  // The same tap can arrive by message and from the cache; only open it once.
  if (id) {
    if (handledIds.has(id)) return true;
    handledIds.add(id);
  }
  if (activeHandler) {
    activeHandler(url);
  } else {
    bufferedUrl = url;
    // Kept until routed, so it isn't lost if this page unloads first (the cached/acknowledged copy is gone).
    saveDeferred(url, id);
  }
  return true;
};

const openPendingCache = () => {
  const cacheStorage = typeof window !== 'undefined' ? window.caches : null;
  return cacheStorage ? cacheStorage.open(PENDING_CACHE_NAME) : Promise.resolve(null);
};

const removePending = async (id) => {
  try {
    const cache = await openPendingCache();
    const response = cache ? await cache.match(PENDING_KEY) : null;
    if (!response) return;
    const pending = await response.json();
    if (!id || pending?.id === id) await cache.delete(PENDING_KEY);
  } catch {
    // Expires on its own.
  }
};

/** Opens a tap the service worker stored (e.g. the app wasn't listening yet, or iOS opened the start page). */
export const consumePendingNavigation = async () => {
  try {
    const cache = await openPendingCache();
    const response = cache ? await cache.match(PENDING_KEY) : null;
    if (!response) return;
    const pending = await response.json();
    // Hand it over (buffered/persisted if the app isn't ready) before removing the worker's copy.
    if (pending && Date.now() - pending.createdAt <= PENDING_MAX_AGE_MS) deliver(pending.url, pending.id);
    await cache.delete(PENDING_KEY);
  } catch {
    // Cache Storage unavailable (e.g. private mode).
  }
};

const handleMessage = (event) => {
  const data = event.data;
  if (!data || data.type !== 'open-chat' || !deliver(data.url, data.id)) return;
  // Acknowledge on receipt: the app opens the chat once it's ready, so the worker must not reload the window.
  event.ports?.[0]?.postMessage({ type: 'open-chat-ack' });
  removePending(data.id);
};

const handleResume = () => {
  if (document.visibilityState !== 'hidden') consumePendingNavigation();
};

/** Starts listening for notification taps. Call once at bootstrap, before rendering. */
export const startPushNavigation = () => {
  if (container || typeof window === 'undefined' || typeof navigator === 'undefined') return;
  const serviceWorker = navigator.serviceWorker;
  if (!serviceWorker) return;
  container = serviceWorker;
  restoreDeferred();
  serviceWorker.addEventListener('message', handleMessage);
  // Deliver messages queued while the page was loading; otherwise they're dispatched before React listens.
  if (typeof serviceWorker.startMessages === 'function') serviceWorker.startMessages();
  document.addEventListener('visibilitychange', handleResume);
  window.addEventListener('pageshow', handleResume);
  consumePendingNavigation();
};

/**
 * Registers the function that routes to a tapped chat (once the router and auth are ready). Any tap that
 * arrived earlier is delivered immediately. Returns an unsubscribe function.
 */
export const subscribePushNavigation = (handler) => {
  activeHandler = handler;
  if (bufferedUrl) {
    const url = bufferedUrl;
    bufferedUrl = null;
    clearDeferred();
    handler(url);
  }
  return () => {
    if (activeHandler === handler) activeHandler = null;
  };
};

export const resetPushNavigationForTests = () => {
  if (container) container.removeEventListener('message', handleMessage);
  document.removeEventListener('visibilitychange', handleResume);
  window.removeEventListener('pageshow', handleResume);
  container = null;
  bufferedUrl = null;
  activeHandler = null;
  handledIds.clear();
};
